import { useCallback, useMemo, useState } from 'react';

import { CommonSelectProps } from '../types/CommonSelectProps';

type SelectProps = Pick<CommonSelectProps, 'hasError'> & {
  value: string;
  setValue: (selectValue: string) => void;
};

type UseSelectSingleReturn = {
  /** Returns false if there are errors */
  validate: () => boolean;
  /** Props to spread in SearchSelect or StandardSelect */
  props: SelectProps;
};

export const useSelectSingle = (
  initialValue?: string,
  onChange?: (selectValue: string, valueChanged?: boolean) => void
): UseSelectSingleReturn => {
  const [selectValue, setSelectValue] = useState<string>(init(initialValue));
  const [hasError, setHasError] = useState(false);

  const props: SelectProps = useMemo(() => {
    const setValue = (value: string) => {
      setSelectValue(value);
      onChange?.(value, value !== selectValue);
      setHasError(false);
    };

    return { hasError, value: selectValue, setValue } satisfies SelectProps;
  }, [hasError, onChange, selectValue]);

  const validate = useCallback(() => {
    if (validateHasSelected(selectValue)) {
      setHasError(false);
      return true;
    } else {
      setHasError(true);
      return false;
    }
  }, [selectValue]);

  return {
    validate,
    props,
  };
};

const validateHasSelected = (selectValue: string): boolean => {
  return !!selectValue;
};

const init = (selectValue?: string): string => {
  return selectValue ?? '';
};
